import React, { useState } from 'react'
import {Shield, Calculator} from 'lucide-react'
import { US_STATES } from '../data/states'
import { calculateMilitaryPaycheck } from '../utils/militaryPayCalculations'

interface MilitaryInputs {
  payGrade: string
  yearsOfService: string
  filingStatus: 'single' | 'married' | 'head-of-household'
  state: string
  bahAmount: string
  basAmount: string
  specialPay: string
}

const PAY_GRADES = [
  // Enlisted
  { grade: 'E-1', label: 'E-1 Private/Seaman Recruit/Airman Basic' },
  { grade: 'E-2', label: 'E-2 Private/Seaman Apprentice/Airman' },
  { grade: 'E-3', label: 'E-3 Private First Class/Seaman/Airman First Class' },
  { grade: 'E-4', label: 'E-4 Specialist/Corporal/Petty Officer 3rd/Senior Airman' },
  { grade: 'E-5', label: 'E-5 Sergeant/Petty Officer 2nd/Staff Sergeant' },
  { grade: 'E-6', label: 'E-6 Staff Sergeant/Petty Officer 1st/Technical Sergeant' },
  { grade: 'E-7', label: 'E-7 Sergeant First Class/Chief Petty Officer/Master Sergeant' },
  { grade: 'E-8', label: 'E-8 Master Sergeant/Senior Chief Petty Officer' },
  { grade: 'E-9', label: 'E-9 Sergeant Major/Master Chief Petty Officer' },
  // Officers
  { grade: 'O-1', label: 'O-1 Second Lieutenant/Ensign' },
  { grade: 'O-2', label: 'O-2 First Lieutenant/Lieutenant Junior Grade' },
  { grade: 'O-3', label: 'O-3 Captain/Lieutenant' },
  { grade: 'O-4', label: 'O-4 Major/Lieutenant Commander' },
  { grade: 'O-5', label: 'O-5 Lieutenant Colonel/Commander' },
  { grade: 'O-6', label: 'O-6 Colonel/Captain' },
]

const MilitaryPayCalculator = () => {
  const [inputs, setInputs] = useState<MilitaryInputs>({
    payGrade: 'E-4',
    yearsOfService: '2',
    filingStatus: 'single',
    state: 'GA',
    bahAmount: '1200',
    basAmount: '460',
    specialPay: '0'
  })

  const [results, setResults] = useState<any>(null)

  const handleCalculate = () => {
    const yearsNum = parseInt(inputs.yearsOfService) || 0
    const bahNum = parseFloat(inputs.bahAmount) || 0
    const basNum = parseFloat(inputs.basAmount) || 0
    const specialPayNum = parseFloat(inputs.specialPay) || 0

    const calculatedResults = calculateMilitaryPaycheck(
      inputs.payGrade,
      yearsNum,
      inputs.filingStatus,
      inputs.state,
      bahNum,
      basNum,
      specialPayNum
    )

    setResults(calculatedResults)
  }

  const handleInputChange = (field: keyof MilitaryInputs, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div id="military-calculator" className="bg-white rounded-2xl shadow-xl p-6 md:p-8 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-8 h-8 text-blue-800" />
        <h2 className="text-2xl font-bold text-gray-900">Army Pay Calc & Military Paycheck Calculator</h2>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Pay Grade */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Pay Grade / Rank <span className="text-red-500">*</span>
          </label>
          <select
            value={inputs.payGrade}
            onChange={(e) => handleInputChange('payGrade', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {PAY_GRADES.map((grade) => (
              <option key={grade.grade} value={grade.grade}>
                {grade.label}
              </option>
            ))}
          </select>
        </div>

        {/* Years of Service */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Years of Service <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            value={inputs.yearsOfService}
            onChange={(e) => handleInputChange('yearsOfService', e.target.value)}
            placeholder="2"
            min="0"
            max="40"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filing Status */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Filing Status <span className="text-red-500">*</span>
          </label>
          <select
            value={inputs.filingStatus}
            onChange={(e) => handleInputChange('filingStatus', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="single">Single</option>
            <option value="married">Married</option>
            <option value="head-of-household">Head of Household</option>
          </select>
        </div>

        {/* State */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            State of Residence <span className="text-red-500">*</span>
          </label>
          <select
            value={inputs.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {US_STATES.map((state) => (
              <option key={state.code} value={state.code}>
                {state.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Allowances Section */}
      <div className="border-t border-gray-200 pt-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Allowances (Tax-Free)</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              BAH (Basic Allowance for Housing)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                value={inputs.bahAmount}
                onChange={(e) => handleInputChange('bahAmount', e.target.value)}
                placeholder="1200"
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              BAS (Basic Allowance for Subsistence)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                value={inputs.basAmount}
                onChange={(e) => handleInputChange('basAmount', e.target.value)}
                placeholder="460"
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Special Pay (if applicable)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                value={inputs.specialPay}
                onChange={(e) => handleInputChange('specialPay', e.target.value)}
                placeholder="0"
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={handleCalculate}
        className="w-full bg-blue-800 text-white font-semibold py-4 rounded-lg hover:bg-blue-900 transition-colors flex items-center justify-center gap-2"
      >
        <Calculator className="w-5 h-5" />
        Calculate Military Take Home Pay
      </button>

      {/* Results */}
      {results && (
        <div className="mt-8 border-t border-gray-200 pt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Military Paycheck Breakdown</h3>
          
          {/* Net Pay Highlight */}
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-6 mb-6">
            <div className="text-sm text-emerald-800 font-medium mb-1">Monthly Take Home Pay</div>
            <div className="text-4xl font-bold text-emerald-900">
              ${results.netPay.toFixed(2)}
            </div>
            <div className="text-sm text-emerald-700 mt-1">after all taxes and deductions</div>
          </div>

          {/* Pay Components */}
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600 mb-1">Base Pay (Taxable)</div>
              <div className="text-2xl font-bold text-blue-900">${results.basePay.toFixed(2)}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600 mb-1">BAH (Tax-Free)</div>
              <div className="text-2xl font-bold text-green-900">${results.bah.toFixed(2)}</div>
            </div>
            <div className="bg-teal-50 rounded-lg p-4">
              <div className="text-sm text-teal-600 mb-1">BAS (Tax-Free)</div>
              <div className="text-2xl font-bold text-teal-900">${results.bas.toFixed(2)}</div>
            </div>
          </div>

          {/* Deductions Grid */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Gross Pay</div>
              <div className="text-2xl font-bold text-gray-900">${results.totalGrossPay.toFixed(2)}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-red-600 mb-1">Total Deductions</div>
              <div className="text-2xl font-bold text-red-900">${results.totalDeductions.toFixed(2)}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-purple-600 mb-1">Federal Tax</div>
              <div className="text-xl font-bold text-purple-900">${results.federalTax.toFixed(2)}</div>
            </div>
            <div className="bg-indigo-50 rounded-lg p-4">
              <div className="text-sm text-indigo-600 mb-1">State Tax</div>
              <div className="text-xl font-bold text-indigo-900">${results.stateTax.toFixed(2)}</div>
            </div>
            <div className="bg-cyan-50 rounded-lg p-4">
              <div className="text-sm text-cyan-600 mb-1">Social Security</div>
              <div className="text-xl font-bold text-cyan-900">${results.socialSecurity.toFixed(2)}</div>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <div className="text-sm text-orange-600 mb-1">Medicare</div>
              <div className="text-xl font-bold text-orange-900">${results.medicare.toFixed(2)}</div>
            </div>
          </div>

          {/* Tax-Free Info */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> BAH and BAS are not subject to federal income tax. Only your base pay and special pay are taxed. Amounts shown are monthly estimates.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default MilitaryPayCalculator
